import api from "@/core/api/axios";
import { AttendantStatus, DocID, ChatWithLastAttendance } from "@/types";
import { PAGINATION } from "@/shared/utils/pagination";
import { phoneSearchNeedle, samePhoneNumber } from "@/shared/utils/phoneMatch";
import type { ChatScopeKeyParams } from "@/features/chat/utils/chatScope";
import log from "loglevel";
import buildApiQueryParams from "../../shared/utils/buildApiQueryParams";
import {
  APIResponseBFFCollection,
  StrapiQueryParams,
  CloseManyRequest,
  CloseManyResult,
} from "@superagents/types/api";

// Poucos candidatos bastam: o filtro por 8 dígitos é estreito e a checagem
// final é local.
const PHONE_LOOKUP_CANDIDATES = 5;

type ChatFilters = NonNullable<StrapiQueryParams<"api::chat.chat">["filters"]>;

/**
 * Cláusula do recorte da lista (SA-1367). O servidor já aplica a visibilidade
 * por área e soma esta cláusula com AND, então o recorte só estreita. Espelha
 * `belongsToScope`, que o socket usa para inserir e remover linhas.
 *
 * Dentro de uma relação o Strapi recusa operador (`$or`) e campo (`end_date`)
 * no mesmo nível (500 na lista): o `$and` junta os dois e mantém as condições
 * na MESMA attendance.
 */
const scopeClause = (
  userId: DocID,
  chatScope?: ChatScopeKeyParams
): ChatFilters | null => {
  if (chatScope?.scope === "assigned") {
    return {
      human_attendances: {
        $and: [
          { end_date: { $null: true } },
          {
            $or: [
              { user: { documentId: { $eq: userId } } },
              { target_user: { documentId: { $eq: userId } } },
            ],
          },
        ],
      },
    };
  }
  if (chatScope?.scope === "areas" && chatScope.areaIds?.length) {
    return {
      human_attendances: {
        end_date: { $null: true },
        target_area: { documentId: { $in: chatScope.areaIds } },
      },
    };
  }
  return null;
};

export const fetchChats = async (
  userId: DocID,
  token: string | undefined,
  paginationOffset: number,
  sectorId?: DocID,
  attendantFilters?: AttendantStatus[],
  search?: string,
  tagIds?: DocID[],
  chatScope?: ChatScopeKeyParams,
  /** Tamanho da página; a contagem do menu pede 1 (só quer o total). */
  limit: number = PAGINATION.defaultLimit,
  /** SA-1455: só as com não lida (o servidor lê a não lida no Redis). */
  unreadOnly = false
) => {
  try {
    const trimmedSearch = search?.trim();
    // Strip de '@' liderante apenas para o filtro de profile_username — sem
    // ele, "@joao" tentaria casar `profile_username = '@joao'` (errado, ja
    // que persistimos sem `@`). Quando o usuario digita SOMENTE '@', o
    // strip resulta em "", o que com `$containsi: ""` casaria com todo
    // contato que tem username — evitamos isso pulando a clausula.
    const profileUsernameSearch = trimmedSearch
      ? trimmedSearch.replace(/^@+/, "")
      : "";

    // Busca: campos do contato (telefone/nome/username) + BSUID via espelho
    // per-setor do chat (client_whatsapp_user_id) — o contato não carrega mais
    // identidade WhatsApp (SA-1118). $or top-level para misturar os dois níveis.
    const searchOr: NonNullable<
      StrapiQueryParams<"api::chat.chat">["filters"]
    >[] = [
      { contact: { phone_number: { $contains: trimmedSearch } } },
      { contact: { name: { $containsi: trimmedSearch } } },
      { client_whatsapp_user_id: { $contains: trimmedSearch } },
    ];
    if (profileUsernameSearch) {
      searchOr.push({
        contact: { profile_username: { $containsi: profileUsernameSearch } },
      });
    }

    let filters: StrapiQueryParams<"api::chat.chat">["filters"] = {
      $or: searchOr,
      attendant_status: {
        $in: attendantFilters,
      },
    };

    // Filtro de tag AND: uma cláusula $and por tag (interseção). NÃO usar $in,
    // que produziria OR (contato com QUALQUER das tags). Combina com os filtros
    // existentes (busca via $or top-level, status) por serem chaves top-level.
    if (tagIds && tagIds.length > 0) {
      const tagAndClauses = tagIds.map((tagId) => ({
        contact: { tags: { documentId: { $eq: tagId } } },
      }));
      filters = {
        ...filters,
        $and: [...((filters?.$and as unknown[]) ?? []), ...tagAndClauses],
      } as StrapiQueryParams<"api::chat.chat">["filters"];
    }

    // Recorte vai no $and, ao lado das tags: as duas cláusulas de `$and` se
    // somam em vez de uma sobrescrever a outra.
    const scope = scopeClause(userId, chatScope);
    if (scope) {
      filters = {
        ...filters,
        $and: [...((filters?.$and as unknown[]) ?? []), scope],
      } as StrapiQueryParams<"api::chat.chat">["filters"];
    }

    const queryParams = buildApiQueryParams<
      "api::chat.chat",
      { unread?: boolean }
    >({
      pagination: {
        start: paginationOffset,
        limit,
        withCount: true,
      },
      filters,
      ...(unreadOnly ? { unread: true } : {}),
    });

    const url = `/chats/sector/${sectorId}?${queryParams}`;

    const response = await api().get<
      APIResponseBFFCollection<"bff::chatWithLastAttendance">
    >(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    log.error(`Erro ao buscar chats para o usuário de id: ${userId}:`, error);
    // Propaga (SA-1252): devolver `{ data: [] }` aqui virava um SUCESSO vazio, e
    // o React Query substituía a lista que o socket manteve quente por nada —
    // marcada fresca por 5min e sem retry. Rejeitando, o cache é preservado e a
    // query tenta de novo.
    throw error;
  }
};

/**
 * Encerradas (SA-1369): `GET /chats/sector/:sectorId/closed`, ordenada pela
 * data do encerramento. Mesmo shape e paginação da lista. Sem `closedBy`
 * (todas as origens, SA-1439); `tagIds` vai repetido (interseção, como a lista
 * principal); `days` fica no padrão do servidor (90) quando ausente (o total
 * do menu pede 7). Sem busca: a busca usa a lista principal.
 */
export const fetchClosedChats = async (
  token: string | undefined,
  sectorId: DocID,
  paginationOffset: number,
  {
    tagIds = [],
    days,
    limit = PAGINATION.defaultLimit,
  }: {
    tagIds?: DocID[];
    days?: number;
    limit?: number;
  } = {}
) => {
  const params = new URLSearchParams();
  for (const tagId of tagIds) params.append("tagIds", tagId);
  if (days !== undefined) params.set("days", String(days));
  params.set("pagination[start]", String(paginationOffset));
  params.set("pagination[limit]", String(limit));
  params.set("pagination[withCount]", "true");

  const response = await api().get<
    APIResponseBFFCollection<"bff::chatWithLastAttendance">
  >(`/chats/sector/${sectorId}/closed?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

type BulkCloseApiResponse = {
  success: boolean;
  code: number;
  message: string;
  data?: CloseManyResult | { errorCode?: string; details?: unknown };
};

export const bulkCloseAttendances = async (
  payload: CloseManyRequest,
  token: string | undefined
): Promise<CloseManyResult> => {
  const response = await api().post<BulkCloseApiResponse>(
    "/human-attendances/close-many",
    payload,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status !== 200 || !response.data?.data) {
    throw new Error(response.data?.message || "Bulk close failed");
  }

  return response.data.data as CloseManyResult;
};

export const fetchChatEntries = async (
  chatId: DocID,
  paginationOffset: number,
  token: string | undefined
) => {
  try {
    const queryParams = buildApiQueryParams<"api::chat.chat">({
      pagination: {
        start: paginationOffset,
        limit: PAGINATION.defaultLimit,
      },
    });

    const response = await api().get<
      APIResponseBFFCollection<"bff::chatEntry">
    >(`/chats/${chatId}/entries?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    log.error(`Erro ao buscar entradas do chat de id: ${chatId}:`, error);
    // Propaga (SA-1252): sucesso vazio aqui apagava a timeline em cache e
    // deixava a conversa vazia por 5min. Ver `fetchChats`.
    throw error;
  }
};

// Busca o chat de um contato específico no setor em uma única chamada.
// Reaproveita GET /chats/sector/:sectorId com filtro Strapi por contato
// (o serviço repassa queryParams.filters direto ao Strapi). Retorna o objeto
// BFF completo (last_message, unread_count, attendant_status) ou null quando
// o contato nunca iniciou uma conversa.
export const fetchChatByDocumentId = async (
  documentId: DocID,
  token: string | undefined
): Promise<ChatWithLastAttendance> => {
  const response = await api().get<{ data: ChatWithLastAttendance }>(
    `/chats/${documentId}/detail`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  return response.data.data;
};

export const fetchChatByContact = async (
  sectorId: DocID,
  contactId: DocID,
  token: string | undefined
): Promise<ChatWithLastAttendance | null> => {
  try {
    const filters: StrapiQueryParams<"api::chat.chat">["filters"] = {
      contact: { documentId: { $eq: contactId } },
    };

    const queryParams = buildApiQueryParams<"api::chat.chat">({
      pagination: { start: 0, limit: 1, withCount: false },
      filters,
    });

    const response = await api().get<
      APIResponseBFFCollection<"bff::chatWithLastAttendance">
    >(`/chats/sector/${sectorId}?${queryParams}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response.data.data[0] ?? null;
  } catch (error) {
    log.error(
      `Erro ao buscar chat do contato ${contactId} no setor ${sectorId}:`,
      error
    );
    return null;
  }
};

/**
 * Conversa existente com um número (SA-1304), para o cartão de contato
 * compartilhado abrir o chat certo em vez de sempre mandar para "nova
 * conversa".
 *
 * O filtro do backend usa os 8 dígitos finais porque é o único trecho que
 * sobrevive a máscara, DDI omitido e 9º dígito; ele traz CANDIDATOS. A decisão
 * final é local, com `samePhoneNumber` — sem isso, um número de outro DDD com
 * o mesmo final abriria a conversa errada.
 */
export const fetchChatByPhone = async (
  sectorId: DocID,
  phoneNumber: string,
  token: string | undefined
): Promise<ChatWithLastAttendance | null> => {
  const needle = phoneSearchNeedle(phoneNumber);
  if (!needle) return null;

  try {
    const filters: StrapiQueryParams<"api::chat.chat">["filters"] = {
      contact: { phone_number: { $contains: needle } },
    };

    const queryParams = buildApiQueryParams<"api::chat.chat">({
      pagination: {
        start: 0,
        limit: PHONE_LOOKUP_CANDIDATES,
        withCount: false,
      },
      filters,
    });

    const response = await api().get<
      APIResponseBFFCollection<"bff::chatWithLastAttendance">
    >(`/chats/sector/${sectorId}?${queryParams}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return (
      response.data.data.find((chat) =>
        samePhoneNumber(chat.contact?.phone_number, phoneNumber)
      ) ?? null
    );
  } catch (error) {
    log.error(`Erro ao buscar chat por telefone no setor ${sectorId}:`, error);
    return null;
  }
};
